import { useState, useEffect, useRef } from "react";
import { Box, Button, Icons, Tabs, Typography } from "fds";
import "./index.scss";
import dayjs from "dayjs";
import { DocumentListResp } from "../../types/files";
import DocumentBottom from "../../components/DocumentBottom";
import useWorkgroupStore from "../../redux/dispatcher/useWorkgroupStore";
import useAdminWorkgroupStore from "../../redux/dispatcher/useAdminWorkgroupStore";
import WorkgroupCheckboxDropdown from "../../components/WorkgroupCheckboxDropdown";
import { Workgroup } from "../../types/workgroup";
import useCheckboxGroup from "../../shared/hooks/useCheckboxGroup";

import {
  getUserInfoFromLocalStorage,
  getWorkspaceCode,
} from "../../shared/utils/auth";
import { LoginDto } from "../../types/auth";
import { SIDE_WORKGROUPLIST_FILTER } from "../../BaseLayout";
import { t } from "i18next";

const ManageStorage = () => {
  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);
  const myId = loginDto?.userInfo?.userId ?? "";
  const INIT_CONTENT_FILTER = {
    date: {
      start: 0,
      end: dayjs().valueOf(),
    },
    folderCode: "",
    masterUserId: getWorkspaceCode() === "MTECOCOMMON00001" ? myId : "",
    order: "datedesc",
    page: 0,
    readType: "",
    search: "",
    size: 50,
    tagCodes: [] as string[],
  }; /** Hook 정리 */
  const { adminWorkgroupStore, getContentList, getWorkgroupList } =
    useAdminWorkgroupStore();
  const { workgroupState, getWorkgroupSideList } = useWorkgroupStore();
  const checkboxGroup = useCheckboxGroup();

  /** State정리 */
  const [tab, changeTab] = useState(0); // 0 : 워크그룹 , 1: My Box 2: 휴지통
  const [filter, changeFilter] = useState(INIT_CONTENT_FILTER);
  const [docInfo, changeDocListInfo] = useState<DocumentListResp>();
  const [buttonClick, setButtonClick] = useState(0);
  const [workgroups, changeWorkgroups] = useState<Workgroup[]>([]);

  useEffect(() => {
    if (tab === 1) {
      changeFilter({
        ...INIT_CONTENT_FILTER,
        tagCodes: tab === 1 ? ["NONEWORKGROUP"] : [],
        readType: "",
        page: 0,
      });
    } else {
      getWorkspaceCode() === "MTECOCOMMON00001"
        ? getWorkgroupSideList(SIDE_WORKGROUPLIST_FILTER)
        : getWorkgroupList({
            orderBy: "workgroupName",
            orderByType: "ASC",
            status: -1,
            posPage: 1,
            pageRange: 1,
            rowRange: 1000,
            searchDate: {
              start: 0,
              end: 0,
            },
            workgroupName: "",
            managerId: "",
            searchMemberIds: [],
            isDetail: "N",
          });
      changeFilter({
        ...INIT_CONTENT_FILTER,
        tagCodes: [],
        readType: tab === 2 ? "trash" : "",
        page: 0,
      });
    }
  }, [tab]);
  useEffect(() => {
    const validWorkgroups = (
      getWorkspaceCode() === "MTECOCOMMON00001"
        ? workgroupState?.workgroupSidemenuList ?? []
        : adminWorkgroupStore?.usingWorkgroup?.workgroupList ?? []
    )?.filter((item: Workgroup) => item.isValidate !== 0);
    changeWorkgroups(validWorkgroups);
    checkboxGroup.handleAllClick([
      ...validWorkgroups?.map((item: any) => item.workgroupId),
    ]);
  }, [
    workgroupState?.workgroupSidemenuList,
    adminWorkgroupStore?.usingWorkgroup,
  ]);
  useEffect(() => {
    getContentList({
      ...filter,
      tagCodes:
        tab === 1
          ? ["NONEWORKGROUP"]
          : filter.tagCodes.filter((item) => item !== "NONEWORKGROUP"),
    });
  }, [filter]);
  useEffect(() => {
    changeDocListInfo(adminWorkgroupStore?.contentList);
  }, [adminWorkgroupStore?.contentList]);

  /** UI: 필터 초기화 상태인지 확인 */
  const isInitFilter = () => {
    return [
      0,
      adminWorkgroupStore?.usingWorkgroup?.workgroupList?.length + 1,
    ].includes(filter?.tagCodes?.length);
  };
  const changeFilterByKey = (key: string, value: any) => {
    if (key === "order") {
      if (value === "file") {
        if (filter.order.includes("date")) {
          value = "filedesc";
        } else {
          value = filter.order === "filedesc" ? "file" : "filedesc";
        }
      } else {
        if (filter.order.includes("name")) {
          value = "datedesc";
        } else {
          value = filter.order === "datedesc" ? "date" : "datedesc";
        }
      }
      changeFilter({ ...filter, order: value, page: 0 });
    } else if (key === "page") {
      if (
        docInfo &&
        docInfo?.docs?.doc &&
        docInfo?.docs?.doc.length < docInfo?.total
      ) {
        changeFilter({ ...filter, [key]: value });
      }
      return;
    } else if (key === "refresh") {
      getContentList(filter);
    } else if (key === "workgroupIds") {
      changeFilter({ ...filter, tagCodes: value, page: 0 });
    }
  };
  const handleInitFilter = () => {
    changeFilter({
      ...INIT_CONTENT_FILTER,
      tagCodes: tab === 1 ? ["NONEWORKGROUP"] : [],
      readType: tab === 2 ? "trash" : "",
      page: 0,
    });
  };
  /** UI : 즐겨찾기  상단 필터 */
  const renderFilter = () => {
    return (
      <Box classes="fav-filter">
        <Button
          leadingIcon={<Icons variant="line" label="redoLeft" />}
          btnStyle="icon"
          size={"sm"}
          onClick={handleInitFilter}
          state={isInitFilter() ? "disable" : "default"}
        />
        <WorkgroupCheckboxDropdown
          buttonClick={buttonClick}
          meetingFilter={filter}
          workgroups={workgroups}
          tab={tab === 0 ? 1 : 0}
          changeFilterByKey={changeFilterByKey}
          setButtonClick={setButtonClick}
        />
      </Box>
    );
  };
  return (
    <Box width={"100%"} height={"100%"} classes={"fav body"}>
      <Box height={96} classes={"layout-title"} justifyContent="flex-start">
        <Typography type="h2" whiteSpace="pre" style={{ display: "flex" }}>
          {t("storage.menu")}
        </Typography>
      </Box>
      <Tabs
        type={"leftFull"}
        selectTab={(tab) => changeTab(tab)}
        selected={tab}
        size={"lg"}
        paddingLeft={32}
        tabs={[t("admin.filter.workgroup"), "My Box", t("trash.title")].map(
          (item) => {
            return { text: item, color: "default", isNew: false };
          }
        )}
      />
      {tab !== 1 && renderFilter()}
      <DocumentBottom
        filter={filter}
        changeFilterByKey={changeFilterByKey}
        height={"calc(100% - 210px)"}
        docInfo={docInfo}
        isInitFilter={isInitFilter()}
        page={
          tab === 2
            ? "manageStorage_trash"
            : tab === 1
            ? "manageStorage_myBox"
            : "manageStorage"
        }
        endReached={() => changeFilterByKey("page", filter.page + 1)}
      />
    </Box>
  );
};

export default ManageStorage;
